export const DEFAULT_BUSINESS_CALCULATOR_INPUTS = {
  qty: 100,
  purchase: 1000,
  deliveryIn: 5000,
  packing: 10000,
  deliveryOut: 400,
  ads: 0,
  tax: 6,
  bank: 4,
  competitor: 1800,
  price: 1800,
  discount: 0,
  rent: 40000,
  salaryOwn: 40000,
  salaryStaff: 25000,
  services: 2500,
  reserve: 10000,
  otherFixed: 0,
  goal: 100000,
};

export const BUSINESS_CALCULATOR_FORMULA_STEPS = [
  {
    marker: '①',
    title: 'Средний чек и прибыль на продажу',
    formula: 'Средний чек = Выручка ÷ Продажи · Прибыль на продажу = (Выручка − Все расходы) ÷ Продажи',
    example: 'Пример: 350 000 ÷ 85 = <b>4 118 ₸</b> чек. (350 000 − 221 000) ÷ 85 = <b>1 518 ₸</b> прибыли с каждой продажи',
  },
  {
    marker: '②',
    title: 'CPS — сколько можно потратить на привлечение одной продажи',
    formula: 'CPS = Средний чек × Желаемая маржинальность%',
    example: 'Пример: 4 118 × 7% = <b>288 ₸</b> — максимум на маркетинг чтобы остаться с нужной маржой',
  },
  {
    marker: '③',
    title: 'CPL — стоимость лида и CPC — стоимость клика',
    formula: 'CPL = CPS × CR2 · CPC = CPL × CR1',
    example: 'Пример: 288 × 50% = <b>144 ₸</b> за лид. 144 × 5% = <b>7 ₸</b> за клик — столько может стоить клик чтобы экономика сходилась',
  },
  {
    marker: '④',
    title: 'План на следующий месяц',
    formula: 'План продаж = (Продажи ÷ Период) × 30 × (1 + Рост%) · Бюджет = План × CPL ÷ CR2',
    example: 'Пример: (85 ÷ 45) × 30 × 1.25 = <b>71 продажа</b>. 71 × 144 ÷ 50% = <b>20 448 ₸</b> рекламного бюджета',
  },
];

export const clampNumber = (value, min, max = Number.MAX_SAFE_INTEGER) => {
  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    return min;
  }

  return Math.min(max, Math.max(min, parsed));
};

export const formatMoney = (value, locale = 'ru-RU') => {
  if (!Number.isFinite(value)) {
    return '—';
  }

  return `${Math.round(value).toLocaleString(locale)} ₸`;
};

export const formatQty = (value, unit, locale = 'ru-RU') => {
  if (!Number.isFinite(value)) {
    return '—';
  }

  return `${Math.round(value).toLocaleString(locale)} ${unit}`;
};

export const formatPercent = (value, digits = 1) => {
  if (!Number.isFinite(value)) {
    return '—';
  }

  return `${value.toFixed(digits)}%`;
};

export const grossAtDiscount = (price, discPct, cogs, delivOut, ads, taxPct, bankPct) => {
  const discountedPrice = price * (1 - discPct / 100);
  return (
    discountedPrice
    - cogs
    - delivOut
    - ads
    - (discountedPrice * taxPct) / 100
    - (discountedPrice * bankPct) / 100
  );
};

export const calculateBusinessCalculatorMetrics = (inputs) => {
  const qty = clampNumber(inputs.qty, 1);
  const purchase = clampNumber(inputs.purchase, 0);
  const deliveryIn = clampNumber(inputs.deliveryIn, 0);
  const packing = clampNumber(inputs.packing, 0);
  const deliveryOut = clampNumber(inputs.deliveryOut, 0);
  const ads = clampNumber(inputs.ads, 0);
  const taxPct = clampNumber(inputs.tax, 0, 50);
  const bankPct = clampNumber(inputs.bank, 0, 10);
  const competitor = clampNumber(inputs.competitor, 0);
  const price = clampNumber(inputs.price, 0);
  const discount = clampNumber(inputs.discount, 0, 90);
  const rent = clampNumber(inputs.rent, 0);
  const salaryOwn = clampNumber(inputs.salaryOwn, 0);
  const salaryStaff = clampNumber(inputs.salaryStaff, 0);
  const services = clampNumber(inputs.services, 0);
  const reserve = clampNumber(inputs.reserve, 0);
  const otherFixed = clampNumber(inputs.otherFixed, 0);
  const goal = clampNumber(inputs.goal, 0);

  const cogs = purchase + (deliveryIn + packing) / qty;
  const finalPrice = price * (1 - discount / 100);
  const taxRub = (finalPrice * taxPct) / 100;
  const bankRub = (finalPrice * bankPct) / 100;
  const varPer = deliveryOut + ads + taxRub + bankRub;
  const fullCost = cogs + varPer;
  const gross = finalPrice - fullCost;
  const margin = finalPrice > 0 ? (gross / finalPrice) * 100 : 0;
  const fixed = rent + salaryOwn + salaryStaff + services + reserve + otherFixed;
  const bepQty = gross > 0 ? Math.ceil(fixed / gross) : Infinity;
  const bepRub = Number.isFinite(bepQty) ? bepQty * finalPrice : NaN;
  const goalQty = gross > 0 ? Math.ceil((fixed + goal) / gross) : Infinity;
  const goalRev = Number.isFinite(goalQty) ? goalQty * finalPrice : NaN;
  const goalDaily = Number.isFinite(goalQty) ? goalQty / 30 : NaN;
  const competitorDiff = finalPrice - competitor;

  const discountScenarios = [5, 10, 20].map((disc) => {
    const grossDisc = grossAtDiscount(price, disc, cogs, deliveryOut, ads, taxPct, bankPct);
    const bepDisc = grossDisc > 0 ? Math.ceil(fixed / grossDisc) : Infinity;

    return { disc, gross: grossDisc, bepQty: bepDisc };
  });

  return {
    cogs,
    varPer,
    finalPrice,
    gross,
    margin,
    taxRub,
    bankRub,
    fullCost,
    fixed,
    bepQty,
    bepRub,
    goalQty,
    goalRev,
    goalDaily,
    competitorDiff,
    discountScenarios,
    goal,
  };
};

export const getInsightState = (metrics) => {
  const {
    gross,
    fullCost,
    finalPrice,
    margin,
    bepQty,
    goal,
    goalQty,
    goalDaily,
    discountScenarios,
  } = metrics;

  const g10 = discountScenarios.find((item) => item.disc === 10)?.gross ?? 0;

  if (gross < 0) {
    return {
      variant: 'bad',
      message: 'Бизнес убыточен при текущих параметрах: расходы <b>{totalCost}</b> превышают выручку <b>{revenue}</b>. Нужно снизить себестоимость или поднять цену.',
      vars: { finalPrice, fullCost, loss: Math.abs(gross) },
    };
  }

  if (margin < 10) {
    return {
      variant: 'bad',
      message: 'Реальная маржинальность <b>{realMargin}</b> — очень низкая. Точка безубыточности <b>{bepSales} шт</b>. Небольшой рост расходов или падение продаж переведёт бизнес в минус.',
      vars: { margin, bepQty },
    };
  }

  if (gross > 0 && bepQty < 50) {
    return {
      variant: 'good',
      message: 'Хорошая модель. Точка безубыточности — всего <b>{bepQty} шт</b>. Маржа <b>{margin}</b>. Для цели {goal} нужно продать <b>{goalQty} шт ({goalDaily} шт/день)</b>.',
      vars: { bepQty, margin, goal, goalQty, goalDaily },
    };
  }

  if (g10 < 0) {
    return {
      variant: 'warn',
      message: 'Скидка 10% уже делает каждую продажу убыточной. Будьте осторожны с акциями — маржа <b>{margin}</b> не оставляет пространства для больших скидок.',
      vars: { margin },
    };
  }

  return {
    variant: 'good',
    message: 'Для прибыли <b>{goal}</b> нужно <b>{salesNeeded} продаж</b> в месяц ({salesDaily}/день). Точка безубыточности <b>{bepSales} шт</b>. Запас прочности <b>{safety}</b> — есть риск при просадке продаж.',
    vars: { margin, bepQty, goal, goalQty, goalDaily },
  };
};
